import React, { useState, useEffect } from 'react';
import {
  View, Text, Modal, TouchableOpacity, StyleSheet,
  ScrollView, TextInput, ActivityIndicator,
} from 'react-native';
import type { ProfileConnectionDto } from '../../types/api/appProfile';
import { appConfig } from '../../config/configManager';

export interface ShareReportModalProps {
  visible: boolean;
  onClose: () => void;
  reportName: string;
  onCreatePdf?: () => void;    // Web only — opens report in new tab for print/save
  profileConnections?: ProfileConnectionDto[];
  apiService?: any;
}

type ActionState = 'idle' | 'sending' | 'sent' | 'error';

export const ShareReportModal: React.FC<ShareReportModalProps> = ({
  visible,
  onClose,
  reportName,
  onCreatePdf,
  profileConnections = [],
  apiService,
}) => {
  const [emailExpanded, setEmailExpanded] = useState(false);
  const [emailName, setEmailName] = useState('');
  const [emailAddress, setEmailAddress] = useState('');
  const [emailState, setEmailState] = useState<ActionState>('idle');
  const [emailMsg, setEmailMsg] = useState('');
  const [connStates, setConnStates] = useState<Record<string, ActionState>>({});

  // Reset state each time the modal opens
  useEffect(() => {
    if (!visible) return;
    setEmailExpanded(false);
    setEmailName('');
    setEmailAddress('');
    setEmailState('idle');
    setEmailMsg('');
    setConnStates({});
  }, [visible]);

  const navGreen = appConfig.getNavButtonColor();

  const handleEmailSend = async () => {
    if (!emailAddress.trim()) return;
    setEmailState('sending');
    try {
      // Backend not yet configured — placeholder stub.
      // When ready: await apiService.sendSecureEmail({ name: emailName, email: emailAddress, reportName });
      await new Promise<void>(r => setTimeout(r, 700));
      setEmailState('sent');
      setEmailMsg(`Report shared with ${emailAddress}`);
    } catch {
      setEmailState('error');
      setEmailMsg('Failed to send. Please try again.');
    }
  };

  const handleConnNotify = async (conn: ProfileConnectionDto) => {
    setConnStates(p => ({ ...p, [conn.ConnId]: 'sending' }));
    try {
      // Backend not yet configured — placeholder stub.
      // When ready: await apiService.sendConnectionNotification({ connectionId: conn.ConnId, reportName });
      await new Promise<void>(r => setTimeout(r, 600));
      setConnStates(p => ({ ...p, [conn.ConnId]: 'sent' }));
    } catch {
      setConnStates(p => ({ ...p, [conn.ConnId]: 'error' }));
    }
  };

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <View style={styles.overlay}>
        <View style={styles.dialog}>

          {/* Header */}
          <View style={styles.dialogHeader}>
            <Text style={styles.dialogTitle}>Share Report</Text>
            <TouchableOpacity onPress={onClose} style={styles.closeBtn}>
              <Text style={styles.closeBtnText}>✕</Text>
            </TouchableOpacity>
          </View>
          <Text style={styles.dialogSub} numberOfLines={1}>{reportName}</Text>

          <ScrollView style={styles.scroll} showsVerticalScrollIndicator={false}>

            {/* ── Create PDF (web only) ─────────────────────────────────── */}
            {onCreatePdf && (
              <TouchableOpacity
                style={[styles.row, styles.rowTappable]}
                onPress={() => { onClose(); onCreatePdf(); }}
              >
                <Text style={styles.rowIcon}>📄</Text>
                <View style={styles.rowBody}>
                  <Text style={styles.rowTitle}>Create PDF</Text>
                  <Text style={styles.rowSub}>Open in new tab to print or save as PDF</Text>
                </View>
                <Text style={styles.chevron}>›</Text>
              </TouchableOpacity>
            )}

            {/* ── Secure Email ─────────────────────────────────────────── */}
            <TouchableOpacity style={styles.row} onPress={() => setEmailExpanded(v => !v)}>
              <Text style={styles.rowIcon}>📧</Text>
              <View style={styles.rowBody}>
                <Text style={styles.rowTitle}>Secure Email</Text>
                <Text style={styles.rowSub}>Send report securely to an email address</Text>
              </View>
              <Text style={styles.chevron}>{emailExpanded ? '∨' : '›'}</Text>
            </TouchableOpacity>

            {emailExpanded && (
              <View style={styles.emailPanel}>
                {(emailState === 'sent' || emailState === 'error') ? (
                  <View style={styles.emailFeedback}>
                    <Text style={{ color: emailState === 'sent' ? '#27ae60' : '#e74c3c', fontWeight: '600', fontSize: 14 }}>
                      {emailMsg}
                    </Text>
                    {emailState === 'error' && (
                      <TouchableOpacity onPress={() => setEmailState('idle')} style={{ marginTop: 6 }}>
                        <Text style={{ color: '#3498db', fontSize: 13 }}>Try again</Text>
                      </TouchableOpacity>
                    )}
                  </View>
                ) : (
                  <>
                    <TextInput
                      style={styles.emailInput}
                      value={emailName}
                      onChangeText={setEmailName}
                      placeholder="Recipient name (optional)"
                      placeholderTextColor="#aaa"
                    />
                    <TextInput
                      style={styles.emailInput}
                      value={emailAddress}
                      onChangeText={setEmailAddress}
                      placeholder="Email address"
                      placeholderTextColor="#aaa"
                      keyboardType="email-address"
                      autoCapitalize="none"
                    />
                    <TouchableOpacity
                      style={[styles.sendBtn, { backgroundColor: navGreen },
                        (!emailAddress.trim() || emailState === 'sending') && { opacity: 0.5 }]}
                      disabled={!emailAddress.trim() || emailState === 'sending'}
                      onPress={handleEmailSend}
                    >
                      {emailState === 'sending'
                        ? <ActivityIndicator size="small" color="#fff" />
                        : <Text style={styles.sendBtnText}>Send Secure Email</Text>
                      }
                    </TouchableOpacity>
                  </>
                )}
              </View>
            )}

            {/* ── Profile Connections ───────────────────────────────────── */}
            <View style={styles.sectionDivider}>
              <Text style={styles.sectionDividerText}>PROFILE CONNECTIONS</Text>
            </View>
            {profileConnections.length > 0 ? (
              profileConnections.map(conn => {
                const cs = connStates[conn.ConnId] ?? 'idle';
                const icon = conn.Type === 'Linked' ? '🔗' : '📨';
                const sublabel = conn.Type === 'Secure Email' && conn.Email
                  ? conn.Email
                  : `${conn.Type} · ${conn.Permissions}`;
                return (
                  <View key={conn.ConnId} style={styles.row}>
                    <Text style={styles.rowIcon}>{icon}</Text>
                    <View style={styles.rowBody}>
                      <Text style={styles.rowTitle}>{conn.DName}</Text>
                      <Text style={styles.rowSub}>{sublabel}</Text>
                    </View>
                    {cs === 'idle' && (
                      <TouchableOpacity
                        style={[styles.notifyBtn, { backgroundColor: navGreen }]}
                        onPress={() => handleConnNotify(conn)}
                      >
                        <Text style={styles.notifyBtnText}>Notify</Text>
                      </TouchableOpacity>
                    )}
                    {cs === 'sending' && <ActivityIndicator size="small" color={navGreen} style={{ marginLeft: 8 }} />}
                    {cs === 'sent' && <Text style={styles.sentLabel}>✓ Sent</Text>}
                    {cs === 'error' && <Text style={styles.errorLabel}>Failed</Text>}
                  </View>
                );
              })
            ) : (
              <View style={styles.row}>
                <Text style={styles.rowIcon}>👥</Text>
                <View style={styles.rowBody}>
                  <Text style={styles.rowTitle}>No connections added</Text>
                  <Text style={styles.rowSub}>Add connections in the Connections tab</Text>
                </View>
              </View>
            )}

          </ScrollView>
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: 20,
  },
  dialog: {
    backgroundColor: '#ffffff',
    borderRadius: 12,
    width: '100%',
    maxWidth: 420,
    overflow: 'hidden',
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 4 },
    shadowOpacity: 0.2,
    shadowRadius: 12,
    elevation: 8,
  },
  dialogHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingTop: 16,
    paddingBottom: 4,
  },
  dialogTitle: {
    fontSize: 17,
    fontWeight: '700',
    color: '#2c3e50',
  },
  closeBtn: {
    padding: 6,
  },
  closeBtnText: {
    fontSize: 18,
    color: '#7f8c8d',
    fontWeight: '600',
  },
  dialogSub: {
    fontSize: 13,
    color: '#7f8c8d',
    paddingHorizontal: 16,
    paddingBottom: 10,
  },
  scroll: {
    maxHeight: 420,
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderTopWidth: 1,
    borderTopColor: '#f0f0f0',
  },
  rowTappable: {
    backgroundColor: '#fafbfc',
  },
  rowIcon: {
    fontSize: 20,
    width: 32,
    textAlign: 'center',
  },
  rowBody: {
    flex: 1,
    marginLeft: 12,
  },
  rowTitle: {
    fontSize: 14,
    fontWeight: '600',
    color: '#2c3e50',
  },
  rowSub: {
    fontSize: 12,
    color: '#7f8c8d',
    marginTop: 1,
  },
  chevron: {
    fontSize: 20,
    color: '#bdc3c7',
    marginLeft: 8,
  },
  emailPanel: {
    paddingHorizontal: 16,
    paddingBottom: 14,
    backgroundColor: '#f8f9fa',
    borderTopWidth: 1,
    borderTopColor: '#f0f0f0',
  },
  emailFeedback: {
    paddingVertical: 12,
    alignItems: 'center',
  },
  emailInput: {
    borderWidth: 1,
    borderColor: '#d0d7de',
    borderRadius: 6,
    paddingVertical: 8,
    paddingHorizontal: 10,
    fontSize: 13,
    color: '#2c3e50',
    backgroundColor: '#ffffff',
    marginTop: 10,
  },
  sendBtn: {
    borderRadius: 6,
    paddingVertical: 10,
    alignItems: 'center',
    marginTop: 10,
  },
  sendBtnText: {
    color: '#ffffff',
    fontWeight: '600',
    fontSize: 13,
  },
  sectionDivider: {
    paddingHorizontal: 16,
    paddingTop: 10,
    paddingBottom: 4,
    backgroundColor: '#f8f9fa',
    borderTopWidth: 1,
    borderTopColor: '#f0f0f0',
  },
  sectionDividerText: {
    fontSize: 11,
    fontWeight: '600',
    color: '#95a5a6',
    letterSpacing: 0.5,
  },
  notifyBtn: {
    paddingHorizontal: 12,
    paddingVertical: 5,
    borderRadius: 5,
    marginLeft: 8,
  },
  notifyBtnText: {
    color: '#ffffff',
    fontSize: 12,
    fontWeight: '600',
  },
  sentLabel: {
    color: '#27ae60',
    fontSize: 12,
    fontWeight: '600',
    marginLeft: 8,
  },
  errorLabel: {
    color: '#e74c3c',
    fontSize: 12,
    marginLeft: 8,
  },
});
